import { useEffect, useRef, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { printingKeys } from '@/features/printing/hooks/usePrinting';
import { revalidateAiUnits, revalidatePlanState } from '@/features/billing/hooks/billing-keys';
import { invalidateWhatsappStatus } from '@/features/whatsapp/hooks/whatsapp-keys';
import {
  createThrottledInvalidation,
  invalidateConversations,
  invalidateCustomerAgentStatus,
  type ThrottledInvalidation,
} from '@/features/conversations/hooks/conversations-keys';
import type { PrintJobUpdatedEvent } from '@/features/printing/types/printing.types';
import { ordersKeys } from './useOrders';

/* ==========================================================================
   SignalR Event Payloads (from docs/api-specification.md lines 2469-2551)
   ========================================================================== */

export interface OrderCreatedEvent {
  orderId: string;
  trackingCode: string;
  /**
   * Número do pedido do dia. Nulo só em pedido gravado antes da numeração, e
   * ausente em API anterior a ela.
   */
  dailyNumber?: number | null;
  /**
   * Status com que o pedido foi gravado: `preparing` quando a loja tem o
   * aceite automático ligado e o servidor o aceitou na criação. Nesse caso o
   * `order.preparing` chega logo depois, e o card vai para a aba Preparo pelo
   * mesmo caminho de uma confirmação manual.
   */
  status: 'pending' | 'preparing';
  customerName: string;
  totalAmount: number;
  deliveryMethod: 'delivery' | 'pickup';
  itemCount: number;
  timestamp: string;
}

export interface OrderStatusChangedEvent {
  orderId: string;
  trackingCode: string;
  status: string;
  timestamp: string;
}

/**
 * `billing.plan_changed` (change billing-plans): o plano efetivo do tenant
 * mudou. É campainha: o painel relê assinatura, faturas e direitos pelo REST.
 */
export interface BillingPlanChangedEvent {
  /** O plano efetivo depois da mudança; `null` quando a loja fica sem plano. */
  planCode: string | null;
}

/**
 * `billing.ai_units_updated` (change billing-ai-units): a carteira de
 * atendimentos mudou de estado, levantou um aviso, recebeu um lote ou teve
 * uma compra mudada. É campainha: o painel relê saldo, extrato e compra pelo
 * REST, e não decide nada pelo estado do evento.
 */
export interface BillingAiUnitsUpdatedEvent {
  state: 'ok' | 'low' | 'exhausted';
}

/* ==========================================================================
   Connection status
   ========================================================================== */

export type SignalRStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/* ==========================================================================
   Module-level shared connection
   --------------------------------------------------------------------------
   We share a single HubConnection across all consumers of this hook so that
   mounting the dashboard and the orders page side-by-side does NOT open two
   sockets to /hubs/orders. A simple refcount tears the connection down when
   the last consumer unmounts. The shared connection is keyed by accessToken
   + tenantId, so a token refresh or tenant switch automatically rebuilds it.
   ========================================================================== */

interface SharedConnection {
  key: string;
  connection: HubConnection;
  refCount: number;
  status: SignalRStatus;
  statusListeners: Set<(status: SignalRStatus) => void>;
  newOrderListeners: Set<(event: OrderCreatedEvent) => void>;
  printingJobListeners: Set<(event: PrintJobUpdatedEvent) => void>;
  planChangedListeners: Set<(event: BillingPlanChangedEvent) => void>;
  /** As campainhas das conversas, juntadas em no máximo uma revalidação por segundo. */
  conversationsInvalidation: ThrottledInvalidation;
}

let shared: SharedConnection | null = null;

/**
 * Assinante único da queda de conexão, para analytics. Vive no nível do
 * módulo — como a própria conexão compartilhada — porque o hook é montado
 * por mais de uma tela ao mesmo tempo e emitir por consumidor duplicaria
 * o evento. Registrado por AnalyticsIdentitySync.
 */
let realtimeDisconnectedListener: ((reason?: string) => void) | null = null;

export function setRealtimeDisconnectedListener(
  listener: ((reason?: string) => void) | null,
): void {
  realtimeDisconnectedListener = listener;
}

function notifyStatus(target: SharedConnection, next: SignalRStatus) {
  target.status = next;
  target.statusListeners.forEach((fn) => fn(next));
}

function buildSharedConnection(
  accessToken: string,
  tenantId: string,
  queryClient: QueryClient,
): SharedConnection {
  const connection = new HubConnectionBuilder()
    .withUrl('/hubs/orders', {
      accessTokenFactory: () => accessToken,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(LogLevel.Warning)
    .build();

  const target: SharedConnection = {
    key: `${accessToken}:${tenantId}`,
    connection,
    refCount: 0,
    status: 'connecting',
    statusListeners: new Set(),
    newOrderListeners: new Set(),
    printingJobListeners: new Set(),
    planChangedListeners: new Set(),
    // Change customer-agent-handoff: a lista, o resumo, a conversa aberta e as
    // mensagens. Uma loja com muitas conversas da IA ao mesmo tempo manda
    // dezenas de campainhas por segundo; o painel relê no máximo uma vez.
    conversationsInvalidation: createThrottledInvalidation(() => {
      const tid = useAuthStore.getState().selectedTenant?.id ?? '';
      invalidateConversations(queryClient, tid);
    }),
  };

  // Invalidate uses the *currently* selected tenant, matching useInvalidateOrders.
  // A tenant switch updates selectedTenant before consumers re-mount; the shared
  // connection itself is keyed on user.tenantId (the JWT claim that scopes the
  // server-side group).
  const invalidate = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    if (tid) {
      queryClient.invalidateQueries({ queryKey: ordersKeys.root(tid) });
    }
  };

  // Eventos da impressão (change production-printing): são campainha. O painel
  // revalida pelo REST — estado, cupons do pedido, selos da fila — e nunca lê
  // o estado do próprio evento, a não ser o número do dia e o nome do ponto
  // que o aviso sonoro mostra em qualquer página.
  const invalidatePrinting = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    if (tid) {
      queryClient.invalidateQueries({ queryKey: printingKeys.root(tid) });
    }
  };

  // Change billing-plans: assinatura, faturas e direitos, nunca o resto do
  // billing. O evento só diz que o plano mudou; o que mudou se lê pelo REST.
  const invalidateBillingPlan = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    revalidatePlanState(queryClient, tid);
  };

  // Change whatsapp-channel: `whatsapp.connection_updated` é campainha. O
  // payload traz só `state` e `reason`, e o painel não lê nem isso: relê
  // `GET /whatsapp/status`, que alimenta a faixa e a aba Atendente IA.
  const invalidateWhatsapp = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    invalidateWhatsappStatus(queryClient, tid);
  };

  // Change billing-ai-units: saldo, extrato, a compra aberta e as faturas.
  const invalidateAiUnits = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    revalidateAiUnits(queryClient, tid);
  };

  // Change customer-agent-handoff: o estado do Atendente IA (faixa de estado e
  // cabeçalho do Atendimento). Raro, então sem janela.
  const invalidateCustomerAgent = () => {
    const tid = useAuthStore.getState().selectedTenant?.id ?? '';
    invalidateCustomerAgentStatus(queryClient, tid);
  };

  connection.onreconnecting((err) => {
    console.warn('[orders-hub] reconnecting', err);
    notifyStatus(target, 'reconnecting');
  });
  connection.onreconnected(() => {
    console.info('[orders-hub] reconnected');
    notifyStatus(target, 'connected');
    invalidate();
    invalidatePrinting();
    // Um `billing.plan_changed` ou um `billing.ai_units_updated` pode ter
    // passado durante a queda.
    invalidateBillingPlan();
    invalidateAiUnits();
    // E um `whatsapp.connection_updated` também.
    invalidateWhatsapp();
    // E as campainhas das conversas e do estado do atendente: revalida na hora.
    target.conversationsInvalidation.flush();
    invalidateCustomerAgent();
  });
  connection.onclose((err) => {
    if (err) console.warn('[orders-hub] closed with error', err);
    target.conversationsInvalidation.cancel();
    notifyStatus(target, 'disconnected');
    realtimeDisconnectedListener?.(err instanceof Error ? err.message : undefined);
  });

  connection.on('order.created', (event: OrderCreatedEvent) => {
    invalidate();
    target.newOrderListeners.forEach((fn) => fn(event));
  });
  connection.on('order.preparing', () => invalidate());
  connection.on('order.out_for_delivery', () => invalidate());
  connection.on('order.ready_for_pickup', () => invalidate());
  connection.on('order.completed', () => invalidate());
  connection.on('order.cancelled', () => invalidate());

  connection.on('printing.job_updated', (event: PrintJobUpdatedEvent) => {
    // A listagem traz o selo `printStatus` de cada pedido.
    invalidate();
    invalidatePrinting();
    target.printingJobListeners.forEach((fn) => fn(event));
  });
  // `updateRequired` (change print-agent) viaja aqui: a faixa relê o estado.
  connection.on('printing.agent_status', () => invalidatePrinting());
  // Change print-agent: um pedido de atualização do computador mudou de estado. A
  // releitura de `agent-release` (sob a raiz da impressão) atualiza a lista de
  // computadores e fecha o aviso ou o diálogo pedido em outra aba.
  connection.on('printing.agent_update', () => invalidatePrinting());
  connection.on('printing.status_changed', () => {
    invalidate();
    invalidatePrinting();
  });

  connection.on('billing.plan_changed', (event: BillingPlanChangedEvent) => {
    invalidateBillingPlan();
    target.planChangedListeners.forEach((fn) => fn(event));
  });

  connection.on('whatsapp.connection_updated', () => invalidateWhatsapp());
  // O payload (`BillingAiUnitsUpdatedEvent`) não é lido: a faixa e o card releem o saldo.
  connection.on('billing.ai_units_updated', () => invalidateAiUnits());

  // Change customer-agent-handoff: campainhas do Atendimento. O payload nunca é
  // lido (só ids e nomes de fio, sem nada do cliente): o painel relê pelo REST.
  connection.on('conversation.updated', () => target.conversationsInvalidation.request());
  connection.on('conversation.assigned', () => target.conversationsInvalidation.request());
  connection.on('conversation.message_added', () => target.conversationsInvalidation.request());
  connection.on('customer_agent.status_changed', () => invalidateCustomerAgent());

  void connection
    .start()
    .then(() => {
      console.info('[orders-hub] connected to /hubs/orders for tenant', tenantId);
      notifyStatus(target, 'connected');
    })
    .catch((err) => {
      console.error('[orders-hub] start failed', err);
      notifyStatus(target, 'disconnected');
    });

  return target;
}

function acquire(
  accessToken: string,
  tenantId: string,
  queryClient: QueryClient,
): SharedConnection {
  const key = `${accessToken}:${tenantId}`;

  if (shared && shared.key !== key) {
    // Auth or tenant changed — fire-and-forget the old connection.
    const old = shared;
    shared = null;
    old.conversationsInvalidation.cancel();
    void old.connection.stop().catch(() => {});
  }

  if (!shared) {
    shared = buildSharedConnection(accessToken, tenantId, queryClient);
  }

  shared.refCount += 1;
  return shared;
}

function release(target: SharedConnection) {
  target.refCount -= 1;
  if (target.refCount <= 0 && shared === target) {
    shared = null;
    target.conversationsInvalidation.cancel();
    void target.connection.stop().catch(() => {});
  }
}

/* ==========================================================================
   Hook
   ========================================================================== */

export interface UseOrdersSignalROptions {
  /** Called when a new order arrives */
  onNewOrder?: (event: OrderCreatedEvent) => void;
  /** `printing.job_updated` recebido: o aviso sonoro de cupom sem confirmação. */
  onPrintingJobUpdated?: (event: PrintJobUpdatedEvent) => void;
  /** `billing.plan_changed` recebido, depois de a assinatura, as faturas e os direitos serem revalidados. */
  onPlanChanged?: (event: BillingPlanChangedEvent) => void;
}

export function useOrdersSignalR(options: UseOrdersSignalROptions = {}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<SignalRStatus>(
    () => shared?.status ?? 'disconnected',
  );

  const onNewOrderRef = useRef(options.onNewOrder);
  const onPrintingJobUpdatedRef = useRef(options.onPrintingJobUpdated);
  const onPlanChangedRef = useRef(options.onPlanChanged);
  useEffect(() => {
    onNewOrderRef.current = options.onNewOrder;
    onPrintingJobUpdatedRef.current = options.onPrintingJobUpdated;
    onPlanChangedRef.current = options.onPlanChanged;
  });

  useEffect(() => {
    if (!accessToken || !tenantId) return;

    const target = acquire(accessToken, tenantId, queryClient);

    const statusListener = (next: SignalRStatus) => setStatus(next);
    const newOrderListener = (event: OrderCreatedEvent) => {
      onNewOrderRef.current?.(event);
    };
    const printingJobListener = (event: PrintJobUpdatedEvent) => {
      onPrintingJobUpdatedRef.current?.(event);
    };
    const planChangedListener = (event: BillingPlanChangedEvent) => {
      onPlanChangedRef.current?.(event);
    };

    target.statusListeners.add(statusListener);
    target.newOrderListeners.add(newOrderListener);
    target.printingJobListeners.add(printingJobListener);
    target.planChangedListeners.add(planChangedListener);
    // Sync the snapshot in case the connection was already established
    // before this consumer mounted.
    statusListener(target.status);

    return () => {
      target.statusListeners.delete(statusListener);
      target.newOrderListeners.delete(newOrderListener);
      target.printingJobListeners.delete(printingJobListener);
      target.planChangedListeners.delete(planChangedListener);
      release(target);
    };
  }, [accessToken, tenantId, queryClient]);

  // When the user is logged out (no token/tenant), no consumer holds the
  // shared connection, so report disconnected regardless of stale local state.
  const effectiveStatus: SignalRStatus =
    accessToken && tenantId ? status : 'disconnected';

  return {
    status: effectiveStatus,
    isConnected: effectiveStatus === 'connected',
    connectionState: shared?.connection.state ?? HubConnectionState.Disconnected,
  };
}
